/**
 * Razorpay Payment component / module.
 * Requires Razorpay script in index.html: https://checkout.razorpay.com/v1/checkout.js
 * Env: VITE_RAZORPAY_KEY_ID (Razorpay Key ID from dashboard)
 */

export interface RazorpayPaymentSuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayCheckoutOptions {
  order_id: string;
  amount: number;
  currency?: string;
  name?: string;
  description?: string;
  /** Called when payment succeeds; receives response for verify-payment API */
  onSuccess: (response: RazorpayPaymentSuccess) => void | Promise<void>;
  /** Called when user closes the checkout without paying */
  onDismiss?: () => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: {
      key: string;
      order_id: string;
      amount: number;
      currency?: string;
      name?: string;
      description?: string;
      handler: (response: RazorpayPaymentSuccess) => void;
      modal?: { ondismiss?: () => void };
    }) => { open: () => void };
  }
}

/** Get Razorpay Key ID from env (set in .env as VITE_RAZORPAY_KEY_ID) */
export function getRazorpayKey(): string | undefined {
  return import.meta.env.VITE_RAZORPAY_KEY_ID as string | undefined;
}

// Store callback so Razorpay handler (which may run in different context) can invoke it reliably
let pendingSuccessCallback:
  | ((response: RazorpayPaymentSuccess) => void | Promise<void>)
  | null = null;

/**
 * Open Razorpay checkout modal.
 * Script must be loaded via index.html. Key is read from VITE_RAZORPAY_KEY_ID.
 */
export function openRazorpayCheckout(options: RazorpayCheckoutOptions): void {
  const key = getRazorpayKey();
  if (!key) {
    throw new Error(
      "Razorpay is not configured. Set VITE_RAZORPAY_KEY_ID in your .env"
    );
  }

  const Razorpay = window.Razorpay;
  if (!Razorpay) {
    throw new Error(
      "Razorpay script not loaded. Add script in index.html: https://checkout.razorpay.com/v1/checkout.js"
    );
  }

  pendingSuccessCallback = options.onSuccess;

  const rzp = new Razorpay({
    key,
    order_id: options.order_id,
    amount: options.amount,
    currency: options.currency ?? "INR",
    name: options.name ?? "Jain Boarding Foundation",
    description: options.description ?? "Donation Payment",
    handler: function razorpaySuccessHandler(response: RazorpayPaymentSuccess) {
      const cb = pendingSuccessCallback;
      pendingSuccessCallback = null;
      if (typeof cb === "function") {
        try {
          const result = cb(response);
          if (
            result &&
            typeof (result as Promise<unknown>).then === "function"
          ) {
            (result as Promise<unknown>).catch((err: unknown) =>
              console.error("Razorpay onSuccess error:", err)
            );
          }
        } catch (err) {
          console.error("Razorpay onSuccess error:", err);
        }
      }
    },
    modal: options.onDismiss ? { ondismiss: options.onDismiss } : undefined,
  });

  rzp.open();
}
